import React, { useState } from 'react';
import { Shield, Check, X, Loader2 } from 'lucide-react';
import orgApi from '../api/organizations';

const TEMPLATES = [
  {
    key: 'viewer',
    name: 'Viewer',
    description: 'Read-only access to dashboards, events, assets, and reports',
    permissions: ['events:read', 'assets:read', 'sbom:read', 'reports:read'],
    default: true,
  },
  {
    key: 'analyst',
    name: 'Analyst',
    description: 'Read/write events, playbook approval, chatops execution and viewing',
    permissions: ['events:read', 'events:create', 'assets:read', 'sbom:read', 'playbooks:view', 'playbooks:approve', 'chatops:view', 'chatops:execute', 'reports:read', 'logs:read'],
    default: true,
  },
];

const TemplateRolePrompt = ({ tenantId, adminRoleId, onComplete, onSkip }) => {
  const [selected, setSelected] = useState(TEMPLATES.map(t => t.key));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const toggleTemplate = (key) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const toCreate = TEMPLATES.filter(t => selected.includes(t.key));
      for (const tmpl of toCreate) {
        const payload = {
          name: tmpl.name,
          description: tmpl.description,
          permissions: tmpl.permissions,
          parent_role_id: tmpl.key === 'analyst' ? adminRoleId : null,
        };
        await orgApi.createRole(tenantId, payload);
      }
      onComplete(selected);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create template roles.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-cyan-400" />
            <h3 className="text-sm font-bold text-gray-200">Default Role Templates</h3>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-400">
            Your organization has been created with the <span className="text-amber-400 font-semibold">Admin</span> role.
            Optionally create default roles to get started faster. You can always customize roles later via Role Manager.
          </p>

          {error && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
              {error}
            </div>
          )}

          <div className="space-y-2">
            {TEMPLATES.map(tmpl => {
              const isSelected = selected.includes(tmpl.key);
              return (
                <label
                  key={tmpl.key}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-cyan-500/10 border-cyan-500/30'
                      : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleTemplate(tmpl.key)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${
                        tmpl.key === 'analyst' ? 'text-cyan-400' : 'text-gray-300'
                      }`}>
                        {tmpl.name}
                      </span>
                      {tmpl.key === 'analyst' && (
                        <span className="text-[10px] text-gray-500">inherits from Admin</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{tmpl.description}</p>
                    <p className="text-xs text-gray-600 mt-1">{tmpl.permissions.length} permissions</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-700">
          <button
            onClick={onSkip}
            disabled={creating}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50"
          >
            Skip
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || selected.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {creating ? 'Creating...' : `Create ${selected.length} Role${selected.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateRolePrompt;
